import React, { useCallback, useEffect, useMemo } from "react";

import { isRequirementPolicy } from "@design-controls/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  QButton,
  QCheckbox,
  QFormControl,
  QInput,
  QModal,
  QModalBody,
  QModalHeader,
  QSelect,
  QSpacer,
  QStack,
  QText,
  useToastProvider,
} from "@qualio/ui-components";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useCurrentConfigs } from "../../hooks/useCurrentConfigs";
import { useRequirementModal } from "../../hooks/useRequirementModal";
import { CreateRequirementForm } from "./types";
import { useCreateDesignElement } from "../../hooks/designElement/useCreateDesignElement";
import { useNavigate } from "react-router";

export const RequirementModal: React.FC = () => {
  const { t } = useTranslation();
  const { isOpen, closeModal, type } = useRequirementModal();
  const configs = useCurrentConfigs();
  const { isLoading, mutate } = useCreateDesignElement();
  const navigate = useNavigate();
  const { showToast } = useToastProvider();

  const defaultFormValues = {
    type: undefined,
    title: "",
    addAnother: false,
  };
  const methods = useForm<CreateRequirementForm>({
    defaultValues: defaultFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(CreateRequirementForm),
  });

  useEffect(() => {
    type && methods.setValue("type", type);
  }, [type, methods]);

  const requirementOptions = useMemo(() => {
    return configs.filter(isRequirementPolicy).map((item) => ({
      label: item.label,
      value: item.type,
    }));
  }, [configs]);

  const {
    formState: { errors },
  } = methods;

  const onCloseModal = useCallback(() => {
    methods.reset(defaultFormValues);
    closeModal();
  }, []);

  const onSubmit = useCallback(
    (form: CreateRequirementForm) => {
      const { addAnother, ...payload } = form;

      mutate(payload, {
        onSuccess: (data) => {
          if (addAnother) {
            methods.reset({
              ...defaultFormValues,
              addAnother,
            });
            showToast({
              title: t("common.notifications.createSuccess.title"),
              description: t("common.notifications.createSuccess.description", {
                title: data.title,
              }),
              status: "success",
              duration: 5000,
              "data-cy": "toast-requirement-create-success",
            });
          } else {
            closeModal();
            navigate(`../requirement/${data.id}/edit`);
          }
        },
      });
    },
    [mutate, methods, closeModal],
  );

  return (
    <QModal isOpen={isOpen} onClose={onCloseModal} size="xl">
      <QModalHeader>
        <QText>{t("requirements.create.modalTitle")}</QText>
      </QModalHeader>
      <QModalBody>
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            noValidate
            data-cy="create-requirement"
          >
            <QStack gap="4">
              <QFormControl
                label={t("requirements.create.fields.requirementLevel")}
                isRequired
                isInvalid={!!errors.type}
                error={errors.type?.message}
                data-cy="type"
              >
                <Controller
                  name="type"
                  render={({ field: { onChange, value } }) => (
                    <QSelect
                      options={requirementOptions}
                      clearInputOnSelect={value === undefined}
                      value={value}
                      onChange={(item) => item && onChange(item?.value)}
                    />
                  )}
                />
              </QFormControl>

              <QFormControl
                label={t("requirements.create.fields.requirementName")}
                isRequired
                isInvalid={!!errors.title}
                error={errors.title?.message}
              >
                <Controller
                  name="title"
                  render={({ field: { onChange, value } }) => (
                    <QInput onChange={onChange} value={value} data-cy="title" />
                  )}
                />
              </QFormControl>

              <QStack justifyContent="space-between" direction="row">
                <Controller
                  name="addAnother"
                  render={({ field: { value, onChange } }) => (
                    <QCheckbox
                      size="sm"
                      data-cy="add-another-checkbox"
                      onChange={onChange}
                      value={value}
                    >
                      {t("requirements.create.actions.addAnother")}
                    </QCheckbox>
                  )}
                />

                <QSpacer />
                <QStack direction="row">
                  <QButton
                    variant="outline"
                    data-cy="cancel-button"
                    onClick={onCloseModal}
                    isDisabled={isLoading}
                  >
                    {t("requirements.create.actions.cancel")}
                  </QButton>
                  <QButton
                    data-cy="submit-button"
                    type="submit"
                    isLoading={isLoading}
                  >
                    {t("requirements.create.actions.create")}
                  </QButton>
                </QStack>
              </QStack>
            </QStack>
          </form>
        </FormProvider>
      </QModalBody>
    </QModal>
  );
};
